import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import ConnectionAddonsCard from './ConnectionAddonsCard';
import ConnectionAddonsHistory from './ConnectionAddonsHistory';

const money = value => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format(Number(value || 0));

const date = value => {
  if (!value) return 'Não informado';
  const parsed = new Date(String(value).length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? 'Não informado' : parsed.toLocaleDateString('pt-BR');
};

const STATUS = {
  active: ['Ativa', 'success'],
  trialing: ['Período de teste', 'info'],
  past_due: ['Pagamento pendente', 'warning'],
  canceled: ['Cancelada', 'secondary'],
  lifetime: ['Vitalícia', 'success'],
  paid: ['Pago', 'success'],
  pending: ['Pendente', 'warning'],
  failed: ['Falhou', 'danger'],
  payment_failed: ['Pagamento falhou', 'danger'],
};

const StatusBadge = ({ value }) => {
  const [label, variant] = STATUS[value] || [value || 'Não informado', 'secondary'];
  return <Badge bg={variant}>{label}</Badge>;
};

const Summary = ({ label, value, detail }) => (
  <div style={{ padding: '18px', border: '1px solid #e5e7eb', borderRadius: '14px', background: '#fff' }}>
    <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
    <div style={{ color: '#0f172a', fontSize: '20px', fontWeight: 800, marginTop: '6px' }}>{value}</div>
    {detail && <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{detail}</div>}
  </div>
);

const PlanoAssinaturaTab = () => {
  const navigate = useNavigate();
  const { authState, fetchAndUpdateSubscription } = useAuthContext();
  const subscription = authState.subscription;
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [addons, setAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [changingAddon, setChangingAddon] = useState('');
  const [addonsHistory, setAddonsHistory] = useState([]);
  const [addonsHistoryLoading, setAddonsHistoryLoading] = useState(true);
  const [addonNotice, setAddonNotice] = useState('');

  const loadBilling = useCallback(async requestedPage => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/subscription/billing-history', { params: { page: requestedPage } });
      setBilling(response.data);
      setPage(requestedPage);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível carregar o histórico de pagamentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBilling(1); }, [loadBilling]);

  const loadAddons = useCallback(async () => {
    setAddonsLoading(true);
    try {
      const response = await axios.get('/api/subscription/connection-addons');
      setAddons(response.data?.addons || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível carregar os adicionais de conexão.');
    } finally {
      setAddonsLoading(false);
    }
  }, []);

  useEffect(() => { loadAddons(); }, [loadAddons]);

  const loadAddonsHistory = useCallback(async () => {
    setAddonsHistoryLoading(true);
    try {
      const response = await axios.get('/api/subscription/connection-addons-history');
      setAddonsHistory(response.data?.history || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível carregar o histórico de conexões adicionais.');
    } finally {
      setAddonsHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadAddonsHistory(); }, [loadAddonsHistory]);

  useEffect(() => {
    if (!addons.some(addon => addon.pending_purchase)) return undefined;
    const interval = window.setInterval(() => {
      loadAddons();
      loadAddonsHistory();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [addons, loadAddons, loadAddonsHistory]);

  const changeAddon = async (addon, quantity, password, skipLegacyConfirmation = false) => {
    if (!skipLegacyConfirmation && quantity < addon.extra_quantity && !window.confirm(
      'A redução altera a cobrança contratada. Excluir uma conexão no painel Conexões não cancela este adicional. Deseja continuar?'
    )) return;
    setChangingAddon(addon.key);
    setError('');
    setAddonNotice('');
    try {
      const response = await axios.put(`/api/subscription/connection-addons/${addon.key}`, { quantity, password });
      setAddons(response.data?.addons || []);
      setAddonNotice(response.data?.message || 'Alteracao processada com sucesso.');
      await loadAddonsHistory();
    } catch (requestError) {
      if (requestError.response?.data?.addons) setAddons(requestError.response.data.addons);
      await loadAddonsHistory();
      setError(requestError.response?.data?.message || 'Não foi possível alterar o adicional.');
    } finally {
      setChangingAddon('');
    }
  };

  const renewalDay = useMemo(() => {
    if (!subscription?.current_period_ends_at) return 'Não informado';
    const parsed = new Date(`${subscription.current_period_ends_at}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? 'Não informado' : `Dia ${parsed.getDate()}`;
  }, [subscription?.current_period_ends_at]);

  const refresh = async () => {
    await Promise.all([
      fetchAndUpdateSubscription?.({ silent: true }),
      loadBilling(page),
      loadAddons(),
      loadAddonsHistory(),
    ]);
  };

  if (authState.isFetchingSubscription && !subscription) {
    return <div className="text-center py-5"><Spinner animation="border" /></div>;
  }

  if (!subscription) {
    return (
      <Alert variant="warning">
        Nenhuma assinatura foi encontrada. <Button variant="link" className="p-0 ms-1" onClick={() => navigate('/pricing')}>Conhecer os planos</Button>
      </Alert>
    );
  }

  const invoices = billing?.invoices?.data || [];
  const plan = subscription.plan || {};

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      {addonNotice && <Alert variant={'success'} dismissible onClose={() => setAddonNotice('')}>{addonNotice}</Alert>}
      <section style={{ padding: '24px', borderRadius: '18px', color: '#fff', background: 'linear-gradient(135deg, #172554, #2563eb)' }}>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div style={{ opacity: .75, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>Plano contratado</div>
            <h2 style={{ margin: '6px 0', fontWeight: 800 }}>{plan.name || 'Plano não informado'}</h2>
            <div><StatusBadge value={subscription.status} /></div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" size="sm" onClick={refresh}>Atualizar</Button>
            <Button variant="outline-light" size="sm" onClick={() => navigate('/pricing')}>Ver planos</Button>
          </div>
        </div>
      </section>

      {subscription.payment_grace_active && (
        <Alert variant="warning" className="mb-0">
          Existe um pagamento pendente. O acesso permanece em tolerância até <strong>{date(subscription.grace_period_ends_at)}</strong>.
        </Alert>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px' }}>
        <Summary label="Mensalidade" value={money(plan.price)} detail="Cobrança recorrente" />
        <Summary label="Próxima renovação" value={date(subscription.current_period_ends_at)} detail={renewalDay} />
        <Summary label="Implantação" value={money(billing?.implementation?.amount ?? plan.implementation_price)} detail={billing?.implementation ? <StatusBadge value={billing.implementation.status} /> : 'Cobrança única'} />
        <Summary label="Situação do acesso" value={subscription.access_mode === 'consultorio_only' ? 'Limitado' : 'Liberado'} detail={subscription.payment_grace_active ? `${subscription.grace_days_remaining} dia(s) de tolerância` : 'Conforme o plano contratado'} />
      </div>

      {Array.isArray(plan.features) && plan.features.length > 0 && (
        <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '16px', background: '#fff' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 800, marginBottom: '14px' }}>Recursos incluídos</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
            {plan.features.map(feature => <div key={feature} style={{ color: '#334155', fontSize: '14px' }}>✓ {feature}</div>)}
          </div>
        </section>
      )}

      <ConnectionAddonsCard
        addons={addons}
        loading={addonsLoading}
        changing={changingAddon}
        onChange={(addon, quantity, password) => changeAddon(addon, quantity, password, true)}
      />

      <ConnectionAddonsHistory history={addonsHistory} loading={addonsHistoryLoading} />

      <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '16px', background: '#fff' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Histórico de pagamentos</h3>
            <small className="text-muted">Mensalidades processadas pela Pagar.me</small>
          </div>
          {billing?.implementation?.paid_at && <small className="text-muted">Implantação paga em {date(billing.implementation.paid_at)}</small>}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : invoices.length === 0 ? (
          <div className="text-muted text-center py-4">Nenhuma mensalidade foi registrada até o momento.</div>
        ) : (
          <Table responsive hover className="align-middle mb-3">
            <thead><tr><th>Ciclo</th><th>Valor</th><th>Vencimento</th><th>Pagamento</th><th>Status</th></tr></thead>
            <tbody>
              {invoices.map(invoice => (
                <tr key={invoice.id}>
                  <td>{invoice.cycle_number ? `${invoice.cycle_number}º ciclo` : '—'}</td>
                  <td>{money(invoice.amount)}</td>
                  <td>{date(invoice.due_at || invoice.billing_at)}</td>
                  <td>{invoice.paid_at ? date(invoice.paid_at) : '—'}</td>
                  <td><StatusBadge value={invoice.status} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {(billing?.invoices?.last_page || 1) > 1 && (
          <div className="d-flex justify-content-end gap-2">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1 || loading} onClick={() => loadBilling(page - 1)}>Anterior</Button>
            <span className="align-self-center text-muted small">Página {page} de {billing.invoices.last_page}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= billing.invoices.last_page || loading} onClick={() => loadBilling(page + 1)}>Próxima</Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default PlanoAssinaturaTab;
